import React, { useState } from 'react';
import './PartnerPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { contactService } from '../services/contactService';
import useSEO from '../hooks/useSEO';

function PartnerPage() {
  useSEO({
    title: "Devenir Partenaire | Soutenez l'Excellence Éducative | GSU Les Palmiers",
    description: "Proposez un partenariat institutionnel, académique ou d'entreprise avec le Groupe Scolaire et Universitaire Les Palmiers à Togba.",
    keywords: "devenir partenaire GSU Palmiers, parrainage entreprise Bénin, mécénat éducatif Abomey-Calavi",
    canonicalUrl: "https://gsulespalmiers.bj/partner"
  });

  const [formData, setFormData] = useState({
    nomEntreprise: '',
    secteurActivite: '',
    nomContact: '',
    fonction: '',
    emailProfessionnel: '',
    telephone: '',
    typePartenariat: '',
    budgetEnvisage: '',
    projetPartenariat: '',
    delaiSouhaite: ''
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Formater le message avec toutes les informations du partenariat
      const messageContent = `
DEMANDE DE PARTENARIAT

Entreprise: ${formData.nomEntreprise}
Secteur d'activité: ${formData.secteurActivite}
Contact: ${formData.nomContact}
Fonction: ${formData.fonction}
Type de partenariat: ${formData.typePartenariat}
Budget envisagé: ${formData.budgetEnvisage || 'Non spécifié'}
Délai souhaité: ${formData.delaiSouhaite}

Description du projet:
${formData.projetPartenariat}
      `.trim();

      // Envoyer via le service contact
      await contactService.submit({
        nom: formData.nomEntreprise,
        prenom: formData.nomContact,
        email: formData.emailProfessionnel,
        telephone: formData.telephone,
        sujet: `Demande de partenariat - ${formData.typePartenariat}`,
        message: messageContent
      });

      setSuccessMessage('Votre demande de partenariat a été envoyée avec succès! Nous vous contacterons dans les plus brefs délais.');
      
      // Réinitialiser le formulaire
      setFormData({
        nomEntreprise: '',
        secteurActivite: '',
        nomContact: '',
        fonction: '',
        emailProfessionnel: '',
        telephone: '',
        typePartenariat: '',
        budgetEnvisage: '',
        projetPartenariat: '',
        delaiSouhaite: ''
      });

      // Faire défiler vers le haut pour voir le message de succès
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setErrorMessage('Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="partner-page">
      <Header />
      
      {/* Bouton Retour */}
      <div className="back-button-section">
        <div className="container">
          <button 
            className="btn-back"
            onClick={() => {
              if (window.navigateTo) {
                window.navigateTo('projets');
              } else {
                window.history.back();
              }
            }}
            aria-label="Retour"
          >
            ← Retour
          </button>
        </div>
      </div>
      
      <section className="partner-content section">
        <div className="container">
          <div className="partner-form-container">
            <h1>Devenir partenaire</h1>
            <p className="form-subtitle">
              Rejoignez notre réseau de partenaires engagés pour l'excellence éducative
            </p>

            {successMessage && (
              <div className="alert alert-success">
                ✅ {successMessage}
              </div>
            )}

            {errorMessage && (
              <div className="alert alert-error">
                ❌ {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="partner-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom de l'entreprise *</label>
                  <input
                    type="text"
                    name="nomEntreprise"
                    placeholder="Votre entreprise"
                    value={formData.nomEntreprise}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Secteur d'activité *</label>
                  <select
                    name="secteurActivite"
                    value={formData.secteurActivite}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Choisissez votre secteur</option>
                    <option value="technologie">Technologie</option>
                    <option value="education">Éducation</option>
                    <option value="finance">Finance</option>
                    <option value="construction">Construction</option>
                    <option value="sante">Santé</option>
                    <option value="commerce">Commerce</option>
                    <option value="industrie">Industrie</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom du contact *</label>
                  <input
                    type="text"
                    name="nomContact"
                    placeholder="Nom et prénom"
                    value={formData.nomContact}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Fonction *</label>
                  <input
                    type="text"
                    name="fonction"
                    placeholder="votre fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email professionnel *</label>
                  <input
                    type="email"
                    name="emailProfessionnel"
                    placeholder="contact@entreprise.com"
                    value={formData.emailProfessionnel}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    name="telephone"
                    placeholder="+229 XX XX XX XX XX"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de partenariat *</label>
                  <select
                    name="typePartenariat"
                    value={formData.typePartenariat}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Type de collaboration</option>
                    <option value="financier">Partenariat financier</option>
                    <option value="materiel">Don de matériel</option>
                    <option value="stage">Offre de stages</option>
                    <option value="formation">Formation professionnelle</option>
                    <option value="recherche">Recherche & Développement</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Budget envisagé</label>
                  <select
                    name="budgetEnvisage"
                    value={formData.budgetEnvisage}
                    onChange={handleChange}
                  >
                    <option value="">Fourchette budgétaire</option>
                    <option value="moins-500k">Moins de 500k FCFA</option>
                    <option value="500k-1m">500k - 1M FCFA</option>
                    <option value="1m-5m">1M - 5M FCFA</option>
                    <option value="5m-10m">5M - 10M FCFA</option>
                    <option value="plus-10m">Plus de 10M FCFA</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Décrivez votre projet de partenariat *</label>
                <textarea
                  name="projetPartenariat"
                  placeholder="Décrivez vos besoins, vos objectifs"
                  rows="5"
                  value={formData.projetPartenariat}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Délai souhaité *</label>
                <select
                  name="delaiSouhaite"
                  value={formData.delaiSouhaite}
                  onChange={handleChange}
                  required
                >
                  <option value="">Quand démarrer ?</option>
                  <option value="immediat">Immédiatement</option>
                  <option value="1-mois">Dans 1 mois</option>
                  <option value="3-mois">Dans 3 mois</option>
                  <option value="6-mois">Dans 6 mois</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <button type="submit" className="btn-submit btn-full" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Soumettre la demande de partenariat'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PartnerPage;
